import {Button, IconButton} from '@primer/react'
import {KebabHorizontalIcon, ChevronRightIcon} from '@primer/octicons-react'
import {CollapsiblePanel} from './CollapsiblePanel'
import styles from './Findings.module.css'

type Severity = 'High' | 'Medium' | 'Low'

interface FindingData {
  title: string
  severity: Severity
  category: string
  /** If true, show the expanded view with diff, description and actions */
  expanded?: boolean
  description?: string
}

const PLACEHOLDER_FINDINGS: FindingData[] = [
  {
    title: 'Potential memory leak',
    severity: 'High',
    category: 'performance',
    expanded: true,
    description:
      "Potential memory leak due to improper cleanup of event listeners in the 'GlimmerShine' component. Suggests implementing a 'destroy' lifecycle hook to remove listeners when the component is unmounted.",
  },
  {title: 'Potential prop type mismatch', severity: 'Medium', category: 'accessibility'},
  {title: 'Unnecessary nesting in Button.jsx', severity: 'Low', category: 'code-style'},
]

/** Findings section showing Copilot code-review findings */
export function Findings() {
  return (
    <CollapsiblePanel heading="Findings">
      <div className={styles.list}>
        {PLACEHOLDER_FINDINGS.map(finding =>
          finding.expanded ? (
            <ExpandedFindingCard key={finding.title} finding={finding} />
          ) : (
            <CondensedFindingCard key={finding.title} finding={finding} />
          ),
        )}
      </div>
    </CollapsiblePanel>
  )
}

function SeverityBadge({severity}: {severity: Severity}) {
  const severityClasses: Record<Severity, string> = {
    High: styles.severityHigh,
    Medium: styles.severityMedium,
    Low: styles.severityLow,
  }
  return <span className={severityClasses[severity]}>{severity}</span>
}

function ExpandedFindingCard({finding}: {finding: FindingData}) {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <span className={styles.cardTitle}>{finding.title}</span>
          <span className={styles.cardMeta}>
            <SeverityBadge severity={finding.severity} />
            <span className={styles.cardCategory}>{finding.category}</span>
          </span>
        </div>
        <div className={styles.cardActions}>
          <IconButton icon={KebabHorizontalIcon} aria-label="More options" variant="invisible" size="small" />
          <IconButton icon={ChevronRightIcon} aria-label="View finding" variant="invisible" size="small" />
        </div>
      </div>

      {/* Diff placeholder */}
      <DiffPlaceholder />

      {finding.description && <p className={styles.bodyText}>{finding.description}</p>}

      <p className={styles.suggestedLabel}>Suggested changeset</p>
      <DiffPlaceholder />

      <div className={styles.actionsRow}>
        <Button>Fix with Copilot</Button>
        <Button variant="default">Add to batch</Button>
      </div>

      <div className={styles.sources}>
        <span className={styles.sourceAvatars}>
          <span className={styles.sourceAvatar} />
          <span className={styles.sourceAvatar} />
        </span>
        <span>2 sources</span>
        <span>&middot;</span>
        <span>Finding uses AI. Check for mistakes.</span>
        <span>&middot;</span>
        <a href="#give-feedback" className={styles.feedbackLink}>
          Give feedback
        </a>
      </div>
    </div>
  )
}

function CondensedFindingCard({finding}: {finding: FindingData}) {
  return (
    <div className={styles.condensedCard}>
      <div className={styles.condensedLeft}>
        <span className={styles.condensedTitle}>{finding.title}</span>
        <span className={styles.cardMeta}>
          <SeverityBadge severity={finding.severity} />
          <span className={styles.cardCategory}>{finding.category}</span>
        </span>
      </div>
      <div className={styles.condensedRight}>
        <IconButton icon={KebabHorizontalIcon} aria-label="More options" variant="invisible" size="small" />
        <ChevronRightIcon size={16} className={styles.chevronRight} />
      </div>
    </div>
  )
}

/** Placeholder diff block with fake lines */
function DiffPlaceholder() {
  return (
    <div className={styles.diffBlock}>
      <div className={`${styles.diffLine} ${styles.diffLineContext}`}>
        <span className={styles.diffLineNumber} />
        <span className={styles.diffLineContent}>
          <span className={styles.diffPlaceholder} style={{width: '30%'}} />
          &nbsp;
          <span className={styles.diffPlaceholder} style={{width: '50%'}} />
        </span>
      </div>
      <div className={`${styles.diffLine} ${styles.diffLineRemoved}`}>
        <span className={styles.diffLineNumber}>-</span>
        <span className={styles.diffLineContent}>
          <span className={styles.diffPlaceholder} style={{width: '70%'}} />
        </span>
      </div>
      <div className={`${styles.diffLine} ${styles.diffLineAdded}`}>
        <span className={styles.diffLineNumber}>+</span>
        <span className={styles.diffLineContent}>
          <span className={styles.diffPlaceholder} style={{width: '60%'}} />
        </span>
      </div>
      <div className={`${styles.diffLine} ${styles.diffLineContext}`}>
        <span className={styles.diffLineNumber} />
        <span className={styles.diffLineContent}>
          <span className={styles.diffPlaceholder} style={{width: '40%'}} />
        </span>
      </div>
    </div>
  )
}
